import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  List,
  ListItem,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdminChatConversation, AdminChatMessage } from '../../../types/admin';
import { format } from 'date-fns';

interface ConversationViewerProps {
  open: boolean;
  conversation: AdminChatConversation | null;
  messages: AdminChatMessage[];
  onClose: () => void;
}

/**
 * ConversationViewer - Modal for viewing full chat conversation thread
 *
 * Features:
 * - Chronological message display
 * - Participant avatars
 * - Message timestamps
 * - Flagged message indicators
 */
export default function ConversationViewer({
  open,
  conversation,
  messages,
  onClose,
}: ConversationViewerProps) {
  if (!conversation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AvatarGroup max={3}>
                {conversation.participants.map((participant, index) => (
                  <Avatar
                    key={index}
                    src={participant.picture}
                    alt={participant.name}
                  >
                    {participant.name.charAt(0)}
                  </Avatar>
                ))}
              </AvatarGroup>
              <Box>
                <Typography variant="h6">
                  {conversation.participants.map((p) => p.name).join(', ')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {conversation.message_count} messages
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <Chip
              label={conversation.type === 'agent_client' ? 'Agent-Client' : 'Group'}
              size="small"
              color="primary"
              variant="outlined"
            />
            {conversation.flagged && (
              <Chip
                label="Flagged"
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <List sx={{ p: 0 }}>
          {messages.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No messages to display</Typography>
            </Box>
          ) : (
            messages.map((message, index) => {
              const isFirstInSequence =
                index === 0 || messages[index - 1].sender !== message.sender;
              const isLastInSequence =
                index === messages.length - 1 ||
                messages[index + 1].sender !== message.sender;

              return (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    pb: isLastInSequence ? 2 : 0.5,
                  }}
                >
                  {isFirstInSequence && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Avatar
                        src={message.sender_picture}
                        alt={message.sender}
                        sx={{ width: 32, height: 32 }}
                      >
                        {message.sender.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="bold">
                        {message.sender}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(message.timestamp), 'MMM d, h:mm a')}
                      </Typography>
                    </Box>
                  )}
                  <Paper
                    sx={{
                      p: 1.5,
                      ml: 5,
                      maxWidth: '80%',
                      bgcolor: message.flagged ? 'error.light' : 'grey.100',
                      position: 'relative',
                    }}
                  >
                    <Typography variant="body2">{message.content}</Typography>
                    {message.flagged && (
                      <Chip
                        label="Flagged"
                        size="small"
                        color="error"
                        sx={{ position: 'absolute', top: -8, right: -8 }}
                      />
                    )}
                  </Paper>
                </ListItem>
              );
            })
          )}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
